import { Node, mergeAttributes } from '@tiptap/core';
import type { NodeViewRendererProps } from '@tiptap/core';

// ═══════════════════════════════════════════════════════════════
// Image — obrázek (sekce 2.3 Ilustrace, Fáze 2, Blok 2–5).
//
// Návrhová rozhodnutí:
//   • Data URL (base64) přímo v `src` — symetrické s mammoth importem
//     (convertImage → data: URI) a s docx.js exportem (ImageRun bere
//     dekódovaný buffer). Žádná oddělená OPFS vrstva ve Fázi 2.
//   • Wrap režim (`wrap`) mapuje na chování ve Wordu:
//        inline   – obrázek v řádku textu (jako velký znak)
//        block    – samostatný odstavec, zarovnání dle `align`
//        left/right – obtékání textu (CSS float, DOCX floating anchor)
//   • Velikost (width/height v px) se drží v atributech uzlu a renderuje
//     do <img> — přežije getHTML() → export i round-trip.
//
// renderHTML záměrně produkuje čistý <img data-*> (ne wrapper div), aby
// exportní DOM-walk v src/docx/export.ts našel obrázek jednoduše a aby
// parseHTML zpětně přečetl atributy z <img>.
// ═══════════════════════════════════════════════════════════════

export type ImageWrap = 'inline' | 'block' | 'left' | 'right';
export type ImageAlign = 'left' | 'center' | 'right';

export interface SetImageOptions {
  src: string;
  alt?: string;
  width?: number;
  height?: number;
  wrap?: ImageWrap;
  align?: ImageAlign;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    image: {
      setImage: (options: SetImageOptions) => ReturnType;
    };
  }
}

const MIN_W = 24;
const MAX_W = 2000;

export const Image = Node.create({
  name: 'image',

  // `inline` skupina + inline:true umožní obrázek v řádku textu (wrap=inline);
  // pro block/float ho stejně umíme umístit, ProseMirror to zvládne jako
  // inline uzel na začátku vlastního odstavce.
  inline: true,
  group: 'inline',
  draggable: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      width: {
        default: null,
        parseHTML: (el) => {
          const w = el.getAttribute('width') || (el as HTMLElement).style.width;
          const n = parseInt(w || '', 10);
          return isNaN(n) ? null : n;
        },
        renderHTML: (attrs) => (attrs.width ? { width: String(attrs.width) } : {}),
      },
      height: {
        default: null,
        parseHTML: (el) => {
          const h = el.getAttribute('height') || (el as HTMLElement).style.height;
          const n = parseInt(h || '', 10);
          return isNaN(n) ? null : n;
        },
        renderHTML: (attrs) => (attrs.height ? { height: String(attrs.height) } : {}),
      },
      wrap: {
        default: 'inline',
        parseHTML: (el) => el.getAttribute('data-wrap') || inferWrapFromStyle(el as HTMLElement) || 'inline',
        renderHTML: (attrs) => ({ 'data-wrap': attrs.wrap || 'inline' }),
      },
      align: {
        default: 'left',
        parseHTML: (el) => el.getAttribute('data-align') || 'left',
        renderHTML: (attrs) => ({ 'data-align': attrs.align || 'left' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'img[src]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes, { class: 'doc-image' })];
  },

  addCommands() {
    return {
      setImage:
        (options) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              src: options.src,
              alt: options.alt ?? null,
              width: options.width ?? null,
              height: options.height ?? null,
              wrap: options.wrap ?? 'inline',
              align: options.align ?? 'left',
            },
          }),
    };
  },

  addNodeView() {
    return (props: NodeViewRendererProps) => new ImageNodeView(props);
  },
});

/** Odvodí wrap z inline stylů (pro <img> importované mammothem bez data-wrap). */
function inferWrapFromStyle(el: HTMLElement): ImageWrap | null {
  const fl = el.style.float;
  if (fl === 'left') return 'left';
  if (fl === 'right') return 'right';
  if (el.style.display === 'block') return 'block';
  return null;
}

// ═══════════════════════════════════════════════════════════════
// NodeView — vlastní render obrázku v editoru s:
//   • aplikací wrap/align/velikosti na wrapper i <img>
//   • 4 rohovými úchyty pro změnu velikosti (drag, Shift = bez poměru)
//   • plovoucím ovládacím panelem wrapu (zobrazí se při výběru)
// ═══════════════════════════════════════════════════════════════
class ImageNodeView {
  dom: HTMLElement;
  contentDOM = null;
  private img: HTMLImageElement;
  private toolbar: HTMLElement;
  private node: NodeViewRendererProps['node'];
  private getPos: NodeViewRendererProps['getPos'];
  private editor: NodeViewRendererProps['editor'];
  private aspect = 1;

  constructor({ node, getPos, editor }: NodeViewRendererProps) {
    this.node = node;
    this.getPos = getPos as any;
    this.editor = editor;

    const wrap = document.createElement('span');
    wrap.className = 'doc-image-wrap';

    const img = document.createElement('img');
    img.className = 'doc-image';
    img.src = node.attrs.src;
    if (node.attrs.alt) img.alt = node.attrs.alt;
    img.draggable = false;
    img.addEventListener('load', () => {
      if (img.naturalWidth) this.aspect = img.naturalWidth / img.naturalHeight;
    });
    this.img = img;

    // 4 rohové úchyty
    const handles = document.createElement('span');
    handles.className = 'doc-image-handles';
    (['nw', 'ne', 'sw', 'se'] as const).forEach((corner) => {
      const h = document.createElement('span');
      h.className = `doc-image-handle doc-image-handle-${corner}`;
      h.dataset.corner = corner;
      h.addEventListener('mousedown', (e) => this.startResize(e, corner));
      handles.appendChild(h);
    });

    // Plovoucí panel s volbami obtékání
    this.toolbar = this.buildToolbar();

    wrap.append(img, handles, this.toolbar);
    this.dom = wrap;
    this.applyLayout(node.attrs as any);
  }

  private buildToolbar(): HTMLElement {
    const bar = document.createElement('span');
    bar.className = 'doc-image-toolbar';
    const opts: { wrap: ImageWrap; label: string; title: string }[] = [
      { wrap: 'inline', label: '⌶', title: 'V řádku s textem' },
      { wrap: 'block', label: '▬', title: 'Nad a pod textem' },
      { wrap: 'left', label: '◧', title: 'Obtékat vlevo' },
      { wrap: 'right', label: '◨', title: 'Obtékat vpravo' },
    ];
    opts.forEach((o) => {
      const b = document.createElement('button');
      b.className = 'doc-image-tbtn';
      b.type = 'button';
      b.textContent = o.label;
      b.title = o.title;
      b.dataset.wrap = o.wrap;
      b.addEventListener('mousedown', (e) => e.preventDefault());
      b.addEventListener('click', () => this.setWrap(o.wrap));
      bar.appendChild(b);
    });
    return bar;
  }

  /** Aplikuje wrap/align/velikost na wrapper i <img>. */
  private applyLayout(attrs: {
    width: number | null;
    height: number | null;
    wrap: ImageWrap;
    align: ImageAlign;
  }) {
    const wrap = attrs.wrap || 'inline';
    const w = this.dom;

    // reset
    w.style.float = '';
    w.style.display = '';
    w.style.marginLeft = '';
    w.style.marginRight = '';
    w.style.textAlign = '';
    w.classList.remove('is-inline', 'is-block', 'is-float');

    if (wrap === 'left') {
      w.style.float = 'left';
      w.style.display = 'inline-block';
      w.style.marginRight = '12px';
      w.classList.add('is-float');
    } else if (wrap === 'right') {
      w.style.float = 'right';
      w.style.display = 'inline-block';
      w.style.marginLeft = '12px';
      w.classList.add('is-float');
    } else if (wrap === 'block') {
      w.style.display = 'block';
      w.classList.add('is-block');
      const a = attrs.align || 'left';
      if (a === 'center') { w.style.marginLeft = 'auto'; w.style.marginRight = 'auto'; }
      else if (a === 'right') { w.style.marginLeft = 'auto'; }
    } else {
      w.style.display = 'inline-block';
      w.classList.add('is-inline');
    }

    if (attrs.width) this.img.style.width = attrs.width + 'px';
    else this.img.style.width = '';
    if (attrs.height) this.img.style.height = attrs.height + 'px';
    else this.img.style.height = '';

    // zvýrazni aktivní volbu v panelu
    this.toolbar.querySelectorAll('.doc-image-tbtn').forEach((b) => {
      (b as HTMLElement).classList.toggle('on', (b as HTMLElement).dataset.wrap === wrap);
    });
  }

  private setWrap(wrap: ImageWrap) {
    this.updateAttrs({ wrap });
  }

  private startResize(e: MouseEvent, corner: 'nw' | 'ne' | 'sw' | 'se') {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = this.img.getBoundingClientRect().width;
    const startH = this.img.getBoundingClientRect().height;
    const aspect = startH > 0 ? startW / startH : this.aspect;
    const growsRight = corner === 'ne' || corner === 'se';

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      let newW = growsRight ? startW + dx : startW - dx;
      newW = Math.max(MIN_W, Math.min(MAX_W, Math.round(newW)));
      // Shift = volný poměr; jinak drž aspect
      const keepAspect = !ev.shiftKey;
      const newH = keepAspect ? Math.round(newW / aspect) : Math.round(startH);
      this.img.style.width = newW + 'px';
      this.img.style.height = keepAspect ? newH + 'px' : this.img.style.height;
      this.dom.dataset.pendingW = String(newW);
      this.dom.dataset.pendingH = keepAspect ? String(newH) : '';
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const w = parseInt(this.dom.dataset.pendingW || '', 10);
      const h = parseInt(this.dom.dataset.pendingH || '', 10);
      delete this.dom.dataset.pendingW;
      delete this.dom.dataset.pendingH;
      const patch: any = {};
      if (!isNaN(w)) patch.width = w;
      if (!isNaN(h)) patch.height = h;
      if (Object.keys(patch).length) this.updateAttrs(patch);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  /** Zapíše změněné atributy zpět do dokumentu (transakce). */
  private updateAttrs(patch: Record<string, unknown>) {
    if (typeof this.getPos !== 'function') return;
    const pos = this.getPos();
    if (pos == null) return;
    this.editor.view.dispatch(
      this.editor.view.state.tr.setNodeMarkup(pos, undefined, {
        ...this.node.attrs,
        ...patch,
      }),
    );
  }

  selectNode() {
    this.dom.classList.add('is-selected');
  }
  deselectNode() {
    this.dom.classList.remove('is-selected');
  }

  update(node: NodeViewRendererProps['node']) {
    if (node.type.name !== 'image') return false;
    this.node = node;
    if (this.img.src !== node.attrs.src) this.img.src = node.attrs.src;
    this.applyLayout(node.attrs as any);
    return true;
  }
}
